import type { FunctionComponent } from 'react'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { ButtonLink, Link, Typography } from '@which/seatbelt'

import { ToolWrapper } from '../../../../shared'
import { ImageWithQuestion } from '../../../../shared/components/ImageWithQuestion'
import { Loader } from '../../../../shared/components/Loader'
import { MyAppointment } from '../../../../shared/components/MyAppointment'
import { formatToolName } from '../../../../shared/utils/formatToolName'
import { initaliseDataLayer, pageViewDataLayer } from '../../../../shared/utils/tracking'
import { RebookAppointmentFlow } from '../../pageFlowData'
import { rebookAppointmentData } from './data'
import type { ChangeAppointmentResponse } from './getChangeAppointmentUrl'
import { getChangeAppointmentUrl } from './getChangeAppointmentUrl'
import styles from './RebookAppointment.module.scss'

type RebookAppointmentType = {
  toolName: string
}

type IFrameData =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'loaded'; response: ChangeAppointmentResponse }

export const RebookAppointment: FunctionComponent<RebookAppointmentType> = ({ toolName }) => {
  const location = useLocation()
  const [iFrameData, setIFrameData] = useState<IFrameData>({ state: 'loading' })

  useEffect(() => {
    pageViewDataLayer(RebookAppointmentFlow.VIEW_NAME)
  }, [])

  useEffect(() => {
    const appointmentId = new URLSearchParams(location.search).get('appointmentId')

    if (appointmentId) {
      setIFrameData({ state: 'loading' })

      getChangeAppointmentUrl(appointmentId)
        .then((response) => setIFrameData({ state: 'loaded', response }))
        .catch(() => setIFrameData({ state: 'error' }))
    } else {
      setIFrameData({ state: 'error' })
    }
  }, [location.search])

  const { imgObj, title } = rebookAppointmentData

  return (
    <ToolWrapper>
      <Helmet>
        <meta content="noindex" name="robots" />
        <script>{initaliseDataLayer(formatToolName(toolName))}</script>
        <title>Reschedule Appointment</title>
        <meta content={'Reschedule your appointmentOnline appointment booking'} name="title" />
        <meta content="summary" name="twitter:card" />
        <meta content={'Reschedule your online appointment booking'} name="description" />
        <meta content="website" property="og:type" />
        <meta content={'https://www.which.co.uk/tool/change-appointment'} property="og:url" />
        <meta content={'Reschedule your appointment'} property="og:title" />
        <meta content={'Reschedule your online appointment booking'} property="og:description" />
        <meta content={'https://www.which.co.uk/tool/change-appointment'} property="twitter:url" />
        <meta content={'Reschedule your appointment'} property="twitter:title" />
        <meta
          content={'Reschedule your online appointment booking'}
          property="twitter:description"
        />
      </Helmet>

      {iFrameData.state === 'loading' && <Loader />}
      {iFrameData.state === 'error' && 'an unexpected error happened'}
      {iFrameData.state === 'loaded' &&
        iFrameData.response.isAuthorised &&
        iFrameData.response.isUpcoming && (
          <>
            <ImageWithQuestion image={imgObj} title={title} />
            <iframe
              allow="geolocation"
              className={styles.rebookAppointmentFrame}
              referrerPolicy="no-referrer"
              src={iFrameData.response.changeAppointmentUrl}
              title="Appointment Choice"
            />
          </>
        )}
      {iFrameData.state === 'loaded' &&
        iFrameData.response.isAuthorised &&
        !iFrameData.response.isUpcoming && (
          <div className={styles.rebookAppointment}>
            <Typography
              className={styles.rebookAppointmentTitle}
              tag="h2"
              textStyle="sb-text-heading-large"
            >
              This appointment can’t be amended as it has already taken place
            </Typography>
            <Typography textStyle="sb-text-body-default-regular">
              If you’d like further assistance on this matter, you can{' '}
              <strong>book a follow up appointment</strong> below or in{' '}
              <Link href="/my-appointments">My Appointments</Link>.
            </Typography>

            <MyAppointment
              buttonLink={
                iFrameData.response.isSupported
                  ? `/tool/book-follow-up?appointmentId=${iFrameData.response.caseView.appointment.id}`
                  : '/tool/book-appointment'
              }
              buttonText={
                iFrameData.response.isSupported ? 'Book follow up' : 'Book new appointment'
              }
              caseView={iFrameData.response.caseView}
            />
            <div className={styles.rebookAppointmentContainer}>
              <Typography textStyle="sb-text-body-default-regular">
                <strong>Have a new issue?</strong>
              </Typography>
              <Typography textStyle="sb-text-body-default-regular">
                If you have a different issue, you can make a new appointment.
              </Typography>
              <ButtonLink
                appearance="secondary"
                data-testid="my-appointment-btn-call"
                href="/tool/book-appointment"
              >
                Book an appointment
              </ButtonLink>
            </div>
          </div>
        )}
    </ToolWrapper>
  )
}
